/**
 * Where a clip's bytes ACTUALLY came from when it began sounding (job #202,
 * Tom's ruling r-2026-10-01: the player always plays clips from the cache).
 *
 * `cacheHit` on `audio_play` is `AudioCache.has(id)` — in-memory id
 * membership, not playback. This reads the URL the element was really handed:
 *   - `blob:`  → decoded from IndexedDB (AudioCache.getWavBlobUrl)
 *   - proxy URL, resource-timing says no bytes crossed the wire → HTTP/SW cache
 *   - proxy URL, bytes transferred → network
 *   - otherwise `unknown` (no timing entry yet; never guessed)
 */
export type AudioPlayedFrom = 'indexeddb' | 'http-cache' | 'network' | 'unknown'

interface TimingLike {
  transferSize?: number
  decodedBodySize?: number
  deliveryType?: string
}

export function audioPlayedFrom(
  playedUrl: string | null | undefined,
  timingFor: (url: string) => TimingLike | undefined = latestTiming,
): AudioPlayedFrom {
  if (!playedUrl) return 'unknown'
  if (playedUrl.startsWith('blob:')) return 'indexeddb'
  const t = timingFor(playedUrl)
  if (!t) return 'unknown'
  if (t.deliveryType === 'cache') return 'http-cache'
  if ((t.transferSize ?? 0) > 0) return 'network'
  if ((t.decodedBodySize ?? 0) > 0) return 'http-cache' // bytes arrived with nothing transferred
  return 'unknown'
}

function latestTiming(url: string): TimingLike | undefined {
  try {
    if (typeof performance === 'undefined') return undefined
    const abs = new URL(url, location.href).href
    const entries = performance.getEntriesByName(abs) as PerformanceResourceTiming[]
    return entries.length ? entries[entries.length - 1] : undefined
  } catch {
    return undefined
  }
}
